'use client';

import { c as _c } from "react/compiler-runtime";
import type { SdkWallet } from '@morfi/backend-sdk-v2/client';
import { usdcToD6 } from '@morfi/contract-sdk';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { CreateProposalWalletSelector, type CreatorWalletBalanceState } from '@/components/launchpad/create-proposal-wallet-selector';
import { ProposalBackerRewardsPanel } from '@/components/launchpad/proposal-backer-rewards-panel';
import { AnimatedAmountInput } from '@/components/trade/animated-amount-input';
import { TradeDetails } from '@/components/trade/trade-details';
import { D6_SCALE } from '@/config/solana';
import { useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { maxFoundingFundAmountD6, quoteFoundingFund } from '@/lib/launchpad/founding-fund-quote';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { useProposalFormStore } from '@/stores/proposal-form';
export interface CreateProposalStep3Props {
  eligibleWallets: readonly SdkWallet[];
  selectedWalletId: string | null;
  walletBalancesD6: ReadonlyMap<string, bigint>;
  walletBalanceState: CreatorWalletBalanceState;
  onWalletChange: (walletId: string) => void;
  submitting: boolean;
  feeBpsBacking: number | undefined;
  feeBpsBuy: number | undefined;
  feeBpsSellNormal: number | undefined;
  feeSplitBackersBps: number | undefined;
  creationThresholdD6: string | undefined;
  configStatus: 'loading' | 'ready' | 'unavailable';
}

/**
 * Spec 113 fund step (step 4) — mirrors the launchpad detail screen's fund
 * card: a Cash/balance row, the amount input with quick amounts + Max, and the
 * "Estimated fees" breakdown — WITHOUT the Withdraw tab, the submit button (the
 * dialog footer owns "Create Proposal"), or the outer Panel (it's embedded in
 * the dialog). Funding is market-level, so there is NO outcome picker. The
 * backer-rewards projection sits below, IN its own Panel card.
 */
export function CreateProposalStep3(props) {
  const $ = _c(8);
  const {
    enabled,
    primaryWallet
  } = useMultiWalletFeature();
  let t0;
  if ($[0] !== enabled || $[1] !== primaryWallet || $[2] !== props.eligibleWallets) {
    t0 = enabled ? props.eligibleWallets : primaryWallet !== null && primaryWallet.isAvailable !== false && props.eligibleWallets.some(wallet => wallet.id === primaryWallet.id) ? [primaryWallet] : [];
    $[0] = enabled;
    $[1] = primaryWallet;
    $[2] = props.eligibleWallets;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const wallets = t0;
  const t1 = enabled ? props.selectedWalletId : primaryWallet?.id ?? null;
  let t2;
  if ($[4] !== props || $[5] !== t1 || $[6] !== wallets) {
    t2 = <CreateProposalFundForm {...props} eligibleWallets={wallets} selectedWalletId={t1} />;
    $[4] = props;
    $[5] = t1;
    $[6] = wallets;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export interface CreateProposalFundFormProps extends CreateProposalStep3Props {
  afterAmount?: ReactNode;
  exactAmountD6?: bigint;
  walletLabel?: string;
  /** Operator dashboard: no quick-amount shortcuts. */
  hideQuickAmounts?: boolean;
  /** Operator dashboard: no projected backer-rewards panel. */
  hideBackerRewards?: boolean;
}
export function CreateProposalFundForm(t0) {
  const $ = _c(72);
  const {
    afterAmount,
    exactAmountD6,
    walletLabel,
    hideQuickAmounts: t1,
    hideBackerRewards: t2,
    eligibleWallets,
    selectedWalletId,
    walletBalancesD6,
    walletBalanceState,
    onWalletChange,
    submitting,
    feeBpsBacking,
    feeBpsBuy,
    feeBpsSellNormal,
    feeSplitBackersBps,
    creationThresholdD6,
    configStatus
  } = t0;
  const hideQuickAmounts = t1 === undefined ? false : t1;
  const hideBackerRewards = t2 === undefined ? false : t2;
  const tBack = useTranslations("launchpad.back");
  const tFees = useTranslations("launchpad.fees");
  const {
    formatD6,
    toUsdc,
    currencySymbol,
    exchangeRate,
    currencyDecimals
  } = useFormattedCurrency();
  const store = useProposalFormStore();
  const scopedEligibleWallets = eligibleWallets;
  const scopedSelectedWalletId = selectedWalletId;
  let effectiveBalanceD6;
  let t3;
  let walletReady;
  if ($[0] !== feeBpsBacking || $[1] !== scopedEligibleWallets || $[2] !== scopedSelectedWalletId || $[3] !== walletBalanceState || $[4] !== walletBalancesD6) {
    let t4;
    if ($[8] !== scopedSelectedWalletId) {
      t4 = wallet => wallet.id === scopedSelectedWalletId;
      $[8] = scopedSelectedWalletId;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const selectedWallet = scopedEligibleWallets.find(t4);
    const walletBalanceD6 = selectedWallet === undefined ? undefined : walletBalancesD6.get(selectedWallet.address);
    walletReady = walletBalanceState === "ready" && walletBalanceD6 !== undefined && feeBpsBacking !== undefined;
    effectiveBalanceD6 = walletBalanceD6 ?? BigInt(0);
    t3 = walletReady ? maxFoundingFundAmountD6(effectiveBalanceD6, feeBpsBacking) : BigInt(0);
    $[0] = feeBpsBacking;
    $[1] = scopedEligibleWallets;
    $[2] = scopedSelectedWalletId;
    $[3] = walletBalanceState;
    $[4] = walletBalancesD6;
    $[5] = effectiveBalanceD6;
    $[6] = t3;
    $[7] = walletReady;
  } else {
    effectiveBalanceD6 = $[5];
    t3 = $[6];
    walletReady = $[7];
  }
  const maxFundAmountD6 = t3;
  const maxLocal = Number(maxFundAmountD6) / D6_SCALE * exchangeRate;
  let t4;
  if ($[10] !== maxLocal) {
    t4 = {
      maxLocal
    };
    $[10] = maxLocal;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const quickAmounts = useQuickAmounts(t4);
  let hasAmount;
  let t5;
  if ($[12] !== exactAmountD6 || $[13] !== feeBpsBacking || $[14] !== store.fundAmount || $[15] !== toUsdc) {
    const parsedLocalAmount = Number.parseFloat(store.fundAmount);
    const usdcAmount = Number.isNaN(parsedLocalAmount) ? 0 : toUsdc(parsedLocalAmount);
    hasAmount = exactAmountD6 === undefined ? usdcAmount > 0 : exactAmountD6 > BigInt(0);
    const amountD6 = exactAmountD6 ?? (hasAmount ? BigInt(usdcToD6(usdcAmount).toString(10)) : BigInt(0));
    t5 = feeBpsBacking === undefined ? null : quoteFoundingFund(amountD6, feeBpsBacking);
    $[12] = exactAmountD6;
    $[13] = feeBpsBacking;
    $[14] = store.fundAmount;
    $[15] = toUsdc;
    $[16] = hasAmount;
    $[17] = t5;
  } else {
    hasAmount = $[16];
    t5 = $[17];
  }
  const quote = t5;
  const exceedsBalance = walletReady && quote !== null && quote.totalD6 > effectiveBalanceD6;
  let t6;
  if ($[18] !== store) {
    t6 = function handleAmountChange(raw) {
      store.updateField("fundAmount", raw);
    };
    $[18] = store;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleAmountChange = t6;
  let t7;
  if ($[20] !== store) {
    t7 = function handleQuickAmount(amount) {
      store.updateField("fundAmount", String(amount));
    };
    $[20] = store;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleQuickAmount = t7;
  let t8;
  if ($[22] !== currencyDecimals || $[23] !== maxLocal || $[24] !== store) {
    t8 = function handleMaxClick() {
      const decimalScale = 10 ** currencyDecimals;
      store.updateField("fundAmount", String(Math.floor(maxLocal * decimalScale) / decimalScale));
    };
    $[22] = currencyDecimals;
    $[23] = maxLocal;
    $[24] = store;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const handleMaxClick = t8;
  let t9;
  if ($[26] !== onWalletChange || $[27] !== scopedEligibleWallets || $[28] !== scopedSelectedWalletId || $[29] !== submitting || $[30] !== walletBalanceState || $[31] !== walletBalancesD6 || $[32] !== walletLabel) {
    t9 = <CreateProposalWalletSelector label={walletLabel} wallets={scopedEligibleWallets} selectedWalletId={scopedSelectedWalletId} balancesD6={walletBalancesD6} balanceState={walletBalanceState} disabled={submitting} onWalletChange={onWalletChange} />;
    $[26] = onWalletChange;
    $[27] = scopedEligibleWallets;
    $[28] = scopedSelectedWalletId;
    $[29] = submitting;
    $[30] = walletBalanceState;
    $[31] = walletBalancesD6;
    $[32] = walletLabel;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== exceedsBalance || $[35] !== tBack) {
    t10 = exceedsBalance && <p className="text-caption text-feedback-error-content" role="alert">{tBack("insufficientBalance")}</p>;
    $[34] = exceedsBalance;
    $[35] = tBack;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const t11 = store.fundAmount;
  const t12 = !hideQuickAmounts;
  const t13 = submitting || !walletReady;
  let t14;
  if ($[37] !== tBack) {
    t14 = tBack("amountLabel");
    $[37] = tBack;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== tBack) {
    t15 = tBack("maxButton");
    $[39] = tBack;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== currencySymbol || $[42] !== handleAmountChange || $[43] !== handleMaxClick || $[44] !== handleQuickAmount || $[45] !== quickAmounts || $[46] !== store.fundAmount || $[47] !== t12 || $[48] !== t13 || $[49] !== t14 || $[50] !== t15) {
    t16 = <AnimatedAmountInput value={t11} onChange={handleAmountChange} currencySymbol={currencySymbol} quickAmounts={quickAmounts} onQuickAmount={handleQuickAmount} onMaxClick={handleMaxClick} showQuickAmounts={t12} disabled={t13} inputLabel={t14} maxLabel={t15} />;
    $[41] = currencySymbol;
    $[42] = handleAmountChange;
    $[43] = handleMaxClick;
    $[44] = handleQuickAmount;
    $[45] = quickAmounts;
    $[46] = store.fundAmount;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== formatD6 || $[53] !== hasAmount || $[54] !== quote || $[55] !== tFees) {
    t17 = quote !== null && <TradeDetails feeKind="proposal-fund" feesRowHidden={!hasAmount} items={[{
      label: tFees("netStake"),
      value: formatD6(String(quote.amountD6))
    }, {
      label: tFees("backingFee"),
      value: formatD6(String(quote.feeD6))
    }]} totalLabel={tFees("totalCost")} totalValue={formatD6(String(quote.totalD6))} />;
    $[52] = formatD6;
    $[53] = hasAmount;
    $[54] = quote;
    $[55] = tFees;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== configStatus || $[58] !== creationThresholdD6 || $[59] !== feeBpsBuy || $[60] !== feeBpsSellNormal || $[61] !== feeSplitBackersBps || $[62] !== hideBackerRewards || $[63] !== store.fundAmount) {
    t18 = hideBackerRewards ? null : <ProposalBackerRewardsPanel proposalPubkey="preview" amountInput={store.fundAmount} currentTotalBackedD6="0" viewerBackerShareBalanceD6="0" creationThresholdD6={creationThresholdD6} feeBpsBuy={feeBpsBuy} feeBpsSellNormal={feeBpsSellNormal} feeSplitBackersBps={feeSplitBackersBps} configStatus={configStatus} />;
    $[57] = configStatus;
    $[58] = creationThresholdD6;
    $[59] = feeBpsBuy;
    $[60] = feeBpsSellNormal;
    $[61] = feeSplitBackersBps;
    $[62] = hideBackerRewards;
    $[63] = store.fundAmount;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== afterAmount || $[66] !== t10 || $[67] !== t16 || $[68] !== t17 || $[69] !== t18 || $[70] !== t9) {
    t19 = <div className="flex flex-col gap-3">{t9}{t10}{t16}{afterAmount}{t17}{t18}</div>;
    $[65] = afterAmount;
    $[66] = t10;
    $[67] = t16;
    $[68] = t17;
    $[69] = t18;
    $[70] = t9;
    $[71] = t19;
  } else {
    t19 = $[71];
  }
  return t19;
}